import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { destroyCookie, parseCookies, setCookie } from 'nookies'

import { signOut } from '@/api/user/sign-out'
import { env } from '@/env'

const cookies = parseCookies()
let isRefreshing = false
let failedRequestQueue: {
  onSuccess: (token: string) => void
  onFailure: (error: AxiosError) => void
}[] = []

export const api = axios.create({
  baseURL: env.VITE_API_URL,
  headers: {
    Authorization: `Bearer ${cookies['reactAuth.token']}`,
  },
})

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalConfig = error.config as InternalAxiosRequestConfig<unknown>

    if (error.response?.status === 401) {
      const data = error.response.data as { code?: string }

      // Se o token expirou, tenta renovar
      if (data?.code === 'token.expired') {
        if (!isRefreshing) {
          isRefreshing = true

          try {
            const { 'reactAuth.refreshToken': refreshToken } = parseCookies()

            const response = await api.post('/Auth/refresh', { refreshToken })
            const {
              token,
              refreshToken: newRefreshToken,
              email,
              id,
            } = response.data

            // Atualiza os cookies
            setCookie(undefined, 'reactAuth.token', token, {
              expires: new Date('2238-01-01'),
              path: '/',
            })
            setCookie(undefined, 'reactAuth.refreshToken', newRefreshToken, {
              expires: new Date('2238-01-01'),
              path: '/',
            })
            setCookie(undefined, 'reactAuth.email', email, {
              expires: new Date('2238-01-01'),
              path: '/',
            })
            setCookie(undefined, 'reactAuth.id', id, {
              expires: new Date('2238-01-01'),
              path: '/',
            })

            // Atualiza o header padrão
            api.defaults.headers.Authorization = `Bearer ${token}`

            // Reenvia as requisições que falharam
            failedRequestQueue.forEach((req) => req.onSuccess(token))
            failedRequestQueue = []
          } catch (refreshError) {
            failedRequestQueue.forEach((req) =>
              req.onFailure(refreshError as AxiosError),
            )
            failedRequestQueue = []

            // Se não conseguir renovar, faz logout
            await handleSignOut()
          } finally {
            isRefreshing = false
          }
        }

        // Enquanto está atualizando o token, enfileira as requisições
        return new Promise((resolve, reject) => {
          failedRequestQueue.push({
            onSuccess: (token: string) => {
              if (!originalConfig) {
                return reject(error)
              }

              originalConfig.headers.set('Authorization', `Bearer ${token}`)
              resolve(api(originalConfig))
            },
            onFailure: (err: AxiosError) => {
              reject(err)
            },
          })
        })
      }
    }

    // Se não for 401, apenas rejeita o erro normalmente
    return Promise.reject(error)
  },
)

async function handleSignOut() {
  try {
    const { 'reactAuth.refreshToken': refreshToken } = parseCookies()
    await signOut(refreshToken)
  } catch (err) {
    console.error('Erro ao fazer logout no servidor:', err)
  } finally {
    destroyCookie(undefined, 'reactAuth.token')
    destroyCookie(undefined, 'reactAuth.refreshToken')
    destroyCookie(undefined, 'reactAuth.email')
    destroyCookie(undefined, 'reactAuth.id')

    window.location.href = '/sign-in'
  }
}
